type Component = unknown;
type ButtonProps = {
  loading?: boolean | Record<string, unknown>;
  loadingIcon?: string;
  disabled?: boolean;
};
type SlotNode = { type: unknown; props?: Record<string, unknown> };
type ButtonSlots = { default?: () => SlotNode[]; [name: string]: unknown };
type VueRuntime = {
  computed: <T>(getter: () => T) => { readonly value: T };
  defineComponent: (options: {
    name: string;
    inheritAttrs: boolean;
    props: Record<string, unknown>;
    setup: (
      props: ButtonProps,
      context: { attrs: Record<string, unknown>; slots: ButtonSlots },
    ) => () => unknown;
  }) => Component;
  h: (...args: any[]) => unknown;
};
type ButtonApp = {
  component: {
    (name: string): Component | undefined;
    (name: string, component: Component): ButtonApp;
  };
};

function isLoadingIcon(className: unknown) {
  return typeof className === 'string' && /\bri-(?:refresh|loader|restart|loop|sync)/.test(className);
}

export function createLoadingButton({ computed, defineComponent, h }: VueRuntime, AntButton: Component) {
  return defineComponent({
    name: 'LoadingButton',
    inheritAttrs: false,
    props: {
      loading: {
        type: [Boolean, Object],
        default: false,
      },
      loadingIcon: String,
      disabled: Boolean,
    },
    setup(props, { attrs, slots }) {
      const isLoading = computed(() => props.loading !== false);

      const content = () => {
        const children = slots.default?.() ?? [];
        const loadingIcon =
          isLoading.value &&
          (props.loadingIcon ||
            (children.some((child) => child.type === 'i' && isLoadingIcon(child.props?.class))
              ? undefined
              : 'ri-loader-4-line'));
        if (!loadingIcon) return { children, hasCustomLoadingIcon: false };

        let replaced = false;
        const loadingChildren = children.map((child) => {
          if (replaced || child.type !== 'i') return child;
          replaced = true;
          return h('i', {
            class: [loadingIcon, 'aw-button-loading-icon'],
            'aria-hidden': 'true',
          }) as SlotNode;
        });
        return {
          children: replaced
            ? loadingChildren
            : [
                h('i', {
                  class: [loadingIcon, 'aw-button-loading-icon'],
                  'aria-hidden': 'true',
                }) as SlotNode,
                ...loadingChildren,
              ],
          hasCustomLoadingIcon: true,
        };
      };

      return () => {
        const { children, hasCustomLoadingIcon } = content();
        return h(
          AntButton,
          {
            ...attrs,
            disabled: props.disabled || isLoading.value,
            'aria-busy': isLoading.value || undefined,
            class: [
              attrs.class,
              {
                'aw-button-loading': isLoading.value,
                'aw-button-loading-custom-icon': hasCustomLoadingIcon,
              },
            ],
          },
          { ...slots, default: () => children },
        );
      };
    },
  });
}

export function registerLoadingButton(app: ButtonApp, vue: VueRuntime) {
  const AntButton = app.component('AButton');
  if (!AntButton) return;

  const LoadingButton = createLoadingButton(vue, AntButton);
  app.component('a-button', LoadingButton);
}
